<script setup>
import { onMounted, ref, reactive, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLeadSourcesStore } from 'dashboard/stores/leadSources';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import LeadIntentsAPI from 'dashboard/api/leadIntents';

import SettingsLayout from '../SettingsLayout.vue';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const route = useRoute();
const router = useRouter();
const store = useLeadSourcesStore();
const pipelinesStore = usePipelinesStore();
const { t } = useI18n();

const source = ref(null);
const tab = ref('general');
const saving = ref(false);
const intents = ref([]);

const form = reactive({
  name: '',
  active: true,
  default_pipeline_id: null,
  default_lead_intent_id: null,
});

// Configs específicas por kind - separadas pra evitar tipos confusos no jsonb
// quando o usuário alterna campos. Sempre serializa pra `config` no save.
const capture = reactive({
  allowed_origins: '', // textarea (1 por linha) - site_form/webhook_api
  cron: '', // poller
  endpoint: '', // poller
  headersJson: '{}', // poller - JSON
  idempotency_key_field: '', // poller
});

const rotateDialogRef = ref(null);

const kindMeta = {
  site_form: {
    label: 'LEAD_SOURCES.KIND.SITE_FORM',
    icon: 'i-lucide-globe',
    color: '#0ea5e9',
  },
  webhook_api: {
    label: 'LEAD_SOURCES.KIND.WEBHOOK_API',
    icon: 'i-lucide-webhook',
    color: '#8b5cf6',
  },
  poller: {
    label: 'LEAD_SOURCES.KIND.POLLER',
    icon: 'i-lucide-refresh-cw',
    color: '#f59e0b',
  },
  csv_import: {
    label: 'LEAD_SOURCES.KIND.CSV_IMPORT',
    icon: 'i-lucide-file-spreadsheet',
    color: '#10b981',
  },
  manual: {
    label: 'LEAD_SOURCES.KIND.MANUAL',
    icon: 'i-lucide-hand',
    color: '#64748b',
  },
  ai_conversation: {
    label: 'LEAD_SOURCES.KIND.AI_CONVERSATION',
    icon: 'i-lucide-bot',
    color: '#ec4899',
  },
};

const tabs = computed(() => [
  {
    key: 'general',
    label: t('LEAD_SOURCES.TABS.GENERAL'),
    icon: 'i-lucide-settings',
  },
  {
    key: 'capture',
    label: t('LEAD_SOURCES.TABS.CAPTURE'),
    icon: 'i-lucide-download',
  },
  {
    key: 'routing',
    label: t('LEAD_SOURCES.TABS.ROUTING'),
    icon: 'i-lucide-git-branch',
  },
]);

const pipelineOptions = computed(() => [
  { value: null, label: '- Nenhum -' },
  ...pipelinesStore.list
    .filter(p => p.active)
    .map(p => ({ value: p.id, label: p.name })),
]);

const intentOptions = computed(() => [
  { value: null, label: '- Nenhum -' },
  ...intents.value.map(i => ({ value: i.id, label: i.name })),
]);

const kind = computed(() => source.value?.kind);
const isInternal = computed(() =>
  ['csv_import', 'manual', 'ai_conversation'].includes(kind.value)
);

const breadcrumbItems = computed(() => [
  { label: t('LEAD_SOURCES.HEADER'), routeName: 'settings_lead_sources' },
  { label: source.value?.name || 'Editar fonte' },
]);

const publicEndpointUrl = computed(() => {
  if (!source.value?.public_endpoint) return null;
  return `${window.location.origin}${source.value.public_endpoint}`;
});

const rawRoutingJson = computed({
  get() {
    try {
      return JSON.stringify(source.value?.routing_rules || [], null, 2);
    } catch {
      return '[]';
    }
  },
  set(v) {
    try {
      source.value.routing_rules = JSON.parse(v);
    } catch {
      /* ignora erro de parse durante digitação */
    }
  },
});

async function load() {
  const [s] = await Promise.all([
    store.fetchOne(route.params.sourceId),
    pipelinesStore.fetchAll(),
    loadIntents(),
  ]);
  source.value = s;
  Object.assign(form, {
    name: s.name,
    active: s.active,
    default_pipeline_id: s.default_pipeline_id,
    default_lead_intent_id: s.default_lead_intent_id,
  });
  const cfg = s.config || {};
  capture.allowed_origins = Array.isArray(cfg.allowed_origins)
    ? cfg.allowed_origins.join('\n')
    : '';
  capture.cron = cfg.cron || '';
  capture.endpoint = cfg.endpoint || '';
  capture.headersJson = JSON.stringify(cfg.headers || {}, null, 2);
  capture.idempotency_key_field = cfg.idempotency_key_field || '';
}

async function loadIntents() {
  try {
    const { data } = await LeadIntentsAPI.get();
    intents.value = data;
  } catch (e) {
    intents.value = [];
  }
}

onMounted(load);

function handleBreadcrumbClick(item) {
  if (item.routeName) router.push({ name: item.routeName });
}

function buildConfig() {
  const cfg = { ...(source.value.config || {}) };
  if (kind.value === 'site_form' || kind.value === 'webhook_api') {
    cfg.allowed_origins = capture.allowed_origins
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);
  }
  if (kind.value === 'poller') {
    cfg.cron = capture.cron.trim();
    cfg.endpoint = capture.endpoint.trim();
    cfg.idempotency_key_field = capture.idempotency_key_field.trim();
    try {
      cfg.headers = JSON.parse(capture.headersJson || '{}');
    } catch (e) {
      useAlert('Headers: JSON inválido.');
      throw e;
    }
  }
  return cfg;
}

async function saveGeneral() {
  saving.value = true;
  try {
    await store.update(source.value.id, {
      name: form.name,
      active: form.active,
      default_pipeline_id: form.default_pipeline_id,
      default_lead_intent_id: form.default_lead_intent_id,
    });
    useAlert('Fonte salva.');
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Falha ao salvar.');
  } finally {
    saving.value = false;
  }
}

async function saveCapture() {
  saving.value = true;
  try {
    const config = buildConfig();
    const updated = await store.update(source.value.id, { config });
    source.value = updated;
    useAlert('Captura salva.');
  } catch (e) {
    if (e?.response) useAlert(e?.response?.data?.error || 'Falha ao salvar.');
  } finally {
    saving.value = false;
  }
}

async function saveRouting() {
  saving.value = true;
  try {
    const updated = await store.update(source.value.id, {
      routing_rules: source.value.routing_rules,
    });
    source.value = updated;
    useAlert('Regras salvas.');
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Falha ao salvar.');
  } finally {
    saving.value = false;
  }
}

function copyToClipboard(text) {
  if (!text) return;
  navigator.clipboard?.writeText(text);
  useAlert('Copiado.');
}

function askRotateToken() {
  rotateDialogRef.value?.open();
}

async function confirmRotateToken() {
  try {
    const updated = await store.rotateToken(source.value.id);
    source.value = updated;
    useAlert('Token rotacionado.');
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Falha ao rotacionar token.');
  } finally {
    rotateDialogRef.value?.close();
  }
}

async function runNow() {
  try {
    await store.runNow(source.value.id);
    useAlert(
      'Poller disparado. O resultado aparece no histórico em alguns segundos.'
    );
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Falha ao disparar poller.');
  }
}
</script>

<template>
  <SettingsLayout
    class="w-full max-w-3xl ltr:mr-auto rtl:ml-auto"
    :is-loading="!source"
  >
    <template #header>
      <div class="flex items-center gap-2 w-full justify-between mb-4 min-h-10">
        <Breadcrumb :items="breadcrumbItems" @click="handleBreadcrumbClick" />
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-6 pb-10">
        <!-- Header da fonte -->
        <div class="flex items-center gap-3">
          <span
            class="size-10 rounded-full flex items-center justify-center flex-shrink-0"
            :style="{ background: `${kindMeta[kind]?.color}1f` }"
          >
            <i
              :class="kindMeta[kind]?.icon || 'i-lucide-circle'"
              class="size-5"
              :style="{ color: kindMeta[kind]?.color }"
            />
          </span>
          <div class="min-w-0 flex-1">
            <h1 class="text-heading-3 text-n-slate-12 truncate">
              {{ source?.name }}
            </h1>
            <p class="text-body-small text-n-slate-11">
              <span class="font-mono">{{ source?.slug }}</span>
              <span class="mx-2">·</span>
              {{ t(kindMeta[kind]?.label || kind) }}
            </p>
          </div>
        </div>

        <!-- Tabs -->
        <nav class="flex gap-1 border-b border-n-weak">
          <button
            v-for="tb in tabs"
            :key="tb.key"
            type="button"
            class="flex items-center gap-2 px-3 py-2 text-body-main border-b-2 transition"
            :class="
              tab === tb.key
                ? 'border-n-brand text-n-slate-12 font-medium'
                : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
            "
            @click="tab = tb.key"
          >
            <i :class="tb.icon" class="size-3.5" />
            {{ tb.label }}
          </button>
        </nav>

        <!-- Geral -->
        <div v-if="tab === 'general'" class="flex flex-col gap-4 max-w-2xl">
          <WithLabel
            :label="t('LEAD_SOURCES.FIELDS.NAME')"
            name="name"
            class="w-full"
          >
            <div class="flex-1">
              <Input
                v-model="form.name"
                type="text"
                placeholder="Ex: Form do site principal"
              />
            </div>
          </WithLabel>

          <WithLabel
            :label="t('LEAD_SOURCES.FIELDS.DEFAULT_PIPELINE')"
            name="default_pipeline_id"
            class="w-full"
          >
            <div class="flex-1">
              <SelectInput
                v-model="form.default_pipeline_id"
                :options="pipelineOptions"
                class="w-full [&>select]:w-full"
              />
            </div>
          </WithLabel>

          <WithLabel
            :label="t('LEAD_SOURCES.FIELDS.DEFAULT_INTENT')"
            name="default_lead_intent_id"
            class="w-full"
          >
            <div class="flex-1">
              <SelectInput
                v-model="form.default_lead_intent_id"
                :options="intentOptions"
                class="w-full [&>select]:w-full"
              />
            </div>
          </WithLabel>

          <hr class="border-n-weak" />

          <label class="flex items-start gap-3 cursor-pointer">
            <input v-model="form.active" type="checkbox" class="mt-1" />
            <div>
              <div class="text-body-main font-medium text-n-slate-12">
                Ativa
              </div>
              <p class="text-body-small text-n-slate-11">
                Quando inativa, o endpoint público retorna 404 e o poller não é
                agendado.
              </p>
            </div>
          </label>

          <div class="pt-2">
            <NextButton
              :is-loading="saving"
              label="Salvar alterações"
              @click="saveGeneral"
            />
          </div>
        </div>

        <!-- Captura -->
        <div v-if="tab === 'capture'" class="flex flex-col gap-4 max-w-2xl">
          <!-- Fontes internas: nada pra configurar aqui -->
          <div
            v-if="isInternal"
            class="flex items-start gap-3 p-3 rounded-xl bg-n-alpha-2 border border-n-weak"
          >
            <i class="i-lucide-info size-4 text-n-slate-11 mt-0.5" />
            <p class="text-body-small text-n-slate-12 mb-0">
              Esta é uma fonte interna. Os leads chegam via
              {{
                kind === 'csv_import'
                  ? 'import de CSV'
                  : kind === 'manual'
                    ? 'criação manual no menu Leads'
                    : 'tool da IA durante atendimento'
              }}
              - não há endpoint público nem captura externa.
            </p>
          </div>

          <!-- site_form / webhook_api -->
          <template v-if="kind === 'site_form' || kind === 'webhook_api'">
            <div
              class="rounded-xl border border-n-weak p-4 flex flex-col gap-3"
            >
              <h3 class="text-heading-3 text-n-slate-12 m-0">
                Endpoint público
              </h3>
              <div class="flex items-center gap-2">
                <code
                  class="flex-1 text-body-small font-mono bg-n-alpha-2 px-3 py-2 rounded border border-n-weak truncate"
                  >{{ publicEndpointUrl || '-' }}</code
                >
                <NextButton
                  faded
                  slate
                  size="sm"
                  icon="i-lucide-copy"
                  v-tooltip="t('LEAD_SOURCES.ACTIONS.COPY_ENDPOINT')"
                  @click="copyToClipboard(publicEndpointUrl)"
                />
              </div>

              <div class="flex items-center gap-2">
                <code
                  class="flex-1 text-body-small font-mono bg-n-alpha-2 px-3 py-2 rounded border border-n-weak truncate"
                  >{{ source?.token || '-' }}</code
                >
                <NextButton
                  faded
                  slate
                  size="sm"
                  icon="i-lucide-copy"
                  v-tooltip="t('LEAD_SOURCES.ACTIONS.COPY_TOKEN')"
                  @click="copyToClipboard(source?.token)"
                />
                <NextButton
                  faded
                  amber
                  size="sm"
                  icon="i-lucide-refresh-cw"
                  :label="t('LEAD_SOURCES.ACTIONS.ROTATE_TOKEN')"
                  @click="askRotateToken"
                />
              </div>

              <div
                class="flex items-start gap-2 p-2 rounded-lg bg-n-amber-3 border border-n-amber-7"
              >
                <i
                  class="i-lucide-shield-alert size-4 text-n-amber-11 mt-0.5 flex-shrink-0"
                />
                <p class="text-body-small text-n-amber-12 m-0">
                  {{ t('LEAD_SOURCES.TOKEN_WARNING') }}
                </p>
              </div>
            </div>

            <div class="flex flex-col gap-1.5 w-full">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('LEAD_SOURCES.FIELDS.ALLOWED_ORIGINS') }}
              </label>
              <textarea
                v-model="capture.allowed_origins"
                rows="4"
                class="px-3 py-2 w-full font-mono rounded-xl border resize-y text-body-small border-n-weak bg-n-solid-1 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
                placeholder="https://site.com.br&#10;https://*.site.com.br"
              />
              <p class="mb-0 text-label-small text-n-slate-11">
                Vazio = aceita qualquer origem (CORS permissivo). Use wildcards
                (`*.dominio.com`) para subdomínios.
              </p>
            </div>
          </template>

          <!-- poller -->
          <template v-if="kind === 'poller'">
            <WithLabel
              :label="t('LEAD_SOURCES.FIELDS.CRON')"
              name="cron"
              class="w-full"
            >
              <div class="flex-1">
                <Input
                  v-model="capture.cron"
                  type="text"
                  placeholder="*/5 * * * *"
                />
              </div>
            </WithLabel>

            <WithLabel
              :label="t('LEAD_SOURCES.FIELDS.POLLER_ENDPOINT')"
              name="endpoint"
              class="w-full"
            >
              <div class="flex-1">
                <Input
                  v-model="capture.endpoint"
                  type="text"
                  placeholder="https://api.terceiro.com/leads?since={{cursor}}"
                />
              </div>
            </WithLabel>

            <WithLabel
              :label="t('LEAD_SOURCES.FIELDS.IDEMPOTENCY_FIELD')"
              name="idempotency_key_field"
              class="w-full"
            >
              <div class="flex-1">
                <Input
                  v-model="capture.idempotency_key_field"
                  type="text"
                  placeholder="id"
                />
              </div>
            </WithLabel>

            <WithLabel
              :label="t('LEAD_SOURCES.FIELDS.POLLER_HEADERS')"
              name="headers"
              class="flex flex-col w-full"
            >
              <textarea
                v-model="capture.headersJson"
                rows="6"
                class="w-full font-mono text-body-small rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2"
                placeholder='{ "Authorization": "Bearer xxx" }'
              />
            </WithLabel>

            <div
              class="rounded-xl border border-n-weak p-4 flex items-center justify-between"
            >
              <div>
                <h4 class="text-body-main font-semibold text-n-slate-12 m-0">
                  Disparar manualmente
                </h4>
                <p class="text-body-small text-n-slate-11 m-0">
                  Roda o poller agora para testar a configuração.
                </p>
              </div>
              <NextButton
                faded
                slate
                size="sm"
                icon="i-lucide-play"
                :label="t('LEAD_SOURCES.ACTIONS.RUN_NOW')"
                @click="runNow"
              />
            </div>

            <div
              v-if="source?.last_run_error"
              class="p-3 rounded-xl bg-n-ruby-3 border border-n-ruby-7"
            >
              <p class="text-body-small text-n-ruby-12 m-0 font-mono">
                {{ source.last_run_error }}
              </p>
            </div>
          </template>

          <div v-if="!isInternal" class="pt-2">
            <NextButton
              :is-loading="saving"
              label="Salvar captura"
              @click="saveCapture"
            />
          </div>
        </div>

        <!-- Roteamento -->
        <div v-if="tab === 'routing'" class="flex flex-col gap-3 max-w-2xl">
          <p class="text-body-main text-n-slate-11">
            {{ t('LEAD_SOURCES.ROUTING_HELP') }}
          </p>
          <textarea
            v-model="rawRoutingJson"
            rows="16"
            class="w-full font-mono text-body-small rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2"
            placeholder='[{ "if": { "field": "intent", "op": "eq", "value": "venda" }, "then": { "pipeline_id": 1, "classification": "hot" } }]'
          />
          <div>
            <NextButton
              :is-loading="saving"
              label="Salvar regras"
              @click="saveRouting"
            />
          </div>
        </div>
      </div>
    </template>

    <Dialog
      ref="rotateDialogRef"
      type="alert"
      title="Rotacionar token"
      description="Gera um novo token e invalida o atual. Atualize qualquer cliente/form que esteja usando o token antigo. Esta ação não pode ser desfeita."
      confirm-button-label="Rotacionar"
      cancel-button-label="Cancelar"
      @confirm="confirmRotateToken"
    />
  </SettingsLayout>
</template>
